import { useDisplayedCarouselItem } from '@/hooks/use-displayed-carousel-item';
import { DisplayedCarouselItemProvider } from '@/providers/displayed-carousel-item';
import { styleMaskThemeBackground } from '@/utils/styles';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useScroll } from '@react-spring/web';
import { useContainerSize } from '@rhg/hooks/use-container-size';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useIntersectionObserver } from '@rhg/hooks/use-intersection-observer';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    List,
    ListItem,
    IconButton,
    Theme,
    ButtonBase,
    SxProps,
} from '@rhg/libs/material';
import { grey } from '@rhg/libs/material/colors';
import {
    NumberByBreakpoint,
    getValueByBreakpoint,
    styleNoScrollbar,
    sxAppend,
} from '@rhg/libs/material/sx';
import { WillBeNullProvider } from '@rhg/providers/will-be-null';
import { isSafari } from '@rhg/utils/browser';
import {
    Children,
    CSSProperties,
    RefObject,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { CarouselStatusAnnouncer } from './status-announcer';
import { CarouselProps, CarouselListItemsProps } from './types';

const styleContainer = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
};
const styleList = {
    display: 'grid',
    overflowX: 'scroll',
    overflowY: 'hidden',
    padding: 0,
    overscrollBehaviorX: 'contain',
};
const styleScrollSnap = {
    scrollSnapType: 'x mandatory',
};
const stylePreviewList = { mr: 0 };
const styleItem = (width?: NumberByBreakpoint | number) => ({
    p: 0,
    m: 0,
    scrollSnapAlign: 'start',
    position: 'relative',
    flex: 0,
    // For some reason overriding the width itself doesn't seem to work
    ...(width ? { maxWidth: width, minWidth: width } : undefined),
});
const styleUnclickableItemOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: (theme: Theme) => theme.zIndex.fab - 1,
};
const styleArrowBackIosCorrector = {
    position: 'relative',
    left: (theme: Theme) => `${theme.semantics.Spacing[4]}px`,
};
const styleButton = {
    boxShadow: `0px 1px 4px 0px ${DS_COLOR.BLACK_12P}`,
    pointerEvents: 'auto',
    zIndex: (theme: Theme) => theme.zIndex.fab,
    backgroundColor: styleMaskThemeBackground,
    '&:hover, &:focus': {
        backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'dark' ? 'action.hover' : grey[100],
    },
};
const styleButtonInvisible = { visibility: 'hidden' };

const CONTAINER_WIDTH_FUDGE_FACTOR = 10;
const PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE = 0.7;

const styleSpacingContainer = {
    position: 'relative',
};
const buttonSize = '40px';

const styleButtonContainer = {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'space-between',
    pointerEvents: 'none',
    width: {
        // 24px === mobile page padding value
        xs: `calc(100dvw - 24px - ${buttonSize})`,
        // 30px === 1.5 padding value
        md: `calc(100% + ${buttonSize} + 30px)`,
    },
    px: '20px',
    // md: double padding
    left: { xs: 0, md: '-40px' },
};
const styleButtonContainerInvisible = { opacity: 0 };

function CarouselSpacingContainer({
    children,
    itemSpacing,
    willRemoveFirstAndLastVisibleItemSpacing,
    wrapperSx,
    ...props
}: {
    children: React.ReactNode;
    itemSpacing?: number;
    willRemoveFirstAndLastVisibleItemSpacing?: boolean;
    wrapperSx?: SxProps<Theme>;
}) {
    return willRemoveFirstAndLastVisibleItemSpacing && itemSpacing ? (
        <Box sx={sxAppend(styleSpacingContainer, wrapperSx)} {...props}>
            {children}
        </Box>
    ) : (
        <span {...props} style={wrapperSx as CSSProperties}>
            {children}
        </span>
    );
}

function CarouselListItem({
    itemWidth,
    scrollRef,
    itemWidthValue,
    trackItemScrollButton,
    position,
    children,
}: CarouselListItemsProps) {
    const { formatMessage } = useIntl();
    const [isItemClickable, setIsItemClickable] = useState(true);

    const displayedCarouselProgramContext = useDisplayedCarouselItem();
    const intersectingCallback = useCallback(
        (entry: IntersectionObserverEntry) => {
            const _isItemClickable =
                entry.intersectionRatio >=
                PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE;
            setIsItemClickable(_isItemClickable);
            if (_isItemClickable) {
                displayedCarouselProgramContext?.setDisplayedItemIndex(
                    position
                );
                displayedCarouselProgramContext?.addVisibleItem(position);
            } else {
                displayedCarouselProgramContext?.removeVisibleItem(position);
            }
        },
        [displayedCarouselProgramContext, position]
    );

    const carouselItemRef = useIntersectionObserver({
        intersectingCallback,
        nonIntersectingCallback: () => {
            setIsItemClickable(false);
            displayedCarouselProgramContext?.removeVisibleItem(position);
        },
        observerOptions: {
            rootMargin: '0px',
            threshold: PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE,
        },
    });

    const onItemClick = useCallback(() => {
        if (!isItemClickable) {
            scrollRef.current?.scrollTo({
                left: position * itemWidthValue,
                behavior: 'smooth',
            });
        }
    }, [isItemClickable, itemWidthValue, position, scrollRef]);

    return (
        <ListItem
            sx={styleItem(itemWidth)}
            ref={carouselItemRef}
            role="listitem"
        >
            {children}
            {!isItemClickable && (
                <ButtonBase
                    tabIndex={-1}
                    trackButton={trackItemScrollButton ?? 'NotMapped'}
                    sx={styleUnclickableItemOverlay}
                    onClick={onItemClick}
                    aria-label={formatMessage(
                        commonMessages['common.scroll_into_view']
                    )}
                />
            )}
        </ListItem>
    );
}

/** Shape of the argument `@react-spring/web`'s `useScroll` passes to `onChange`. */
type ScrollChange = {
    value: { scrollX: number; scrollXProgress: number };
};

export function Carousel({
    itemWidth,
    itemSpacing,
    willRemoveFirstAndLastVisibleItemSpacing,
    itemsDisplayed = 1,
    showItemPreview,
    children,
    trackBackButton,
    trackForwardButton,
    trackItemScrollButton = 'NotMapped',
    withAccessibleButtons = true,
    showButtons = true,
    showButtonsWhenDisabled = false,
    toggleButtonsOnHover = false,
    firstItemOffsetPx = 0,
    buttonContainerSx,
    listSx,
    sx,
    emptyState,
    ...props
}: CarouselProps) {
    const scrollRef = useRef<HTMLUListElement>(null);
    const { formatMessage } = useIntl();
    const isSafariUser = isSafari();

    const [scrollXProgress, setScrollXProgress] = useState(0);
    const [scrollX, setScrollX] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const handleHoverStart = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleHoverEnd = useCallback(() => {
        setIsHovered(false);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const { width: containerWidth } = useContainerSize(containerRef);

    const currentBreakpoint = useCurrentBreakpointName();

    const itemWidthValue = useMemo(
        () => getValueByBreakpoint<number>(itemWidth, currentBreakpoint) ?? 0,
        [currentBreakpoint, itemWidth]
    );

    const itemSpacingValue = useMemo(
        () =>
            getValueByBreakpoint<number>(itemSpacing ?? 0, currentBreakpoint) ??
            0,
        [currentBreakpoint, itemSpacing]
    );

    /**
     * This is used to properly align the first actual carousel item.
     * We do this by creating a blank invisible first item in the list with the specified width and padding.
     */
    const firstItemOffsetValue = useMemo(
        () => getValueByBreakpoint(firstItemOffsetPx, currentBreakpoint) ?? 0,
        [currentBreakpoint, firstItemOffsetPx]
    );

    const numItemsDisplayed = useMemo(() => {
        // An unmeasured container and a genuinely 0-width one (a `display: none`
        // ancestor) both fall back to the configured count, rather than the 0
        // items the width math would otherwise yield.
        const maxItems = !containerWidth
            ? Number.MAX_VALUE
            : Math.floor(
                  (containerWidth + CONTAINER_WIDTH_FUDGE_FACTOR) /
                      itemWidthValue
              );
        const desiredItems =
            typeof itemsDisplayed === 'number'
                ? itemsDisplayed
                : itemsDisplayed[currentBreakpoint];
        return Math.min(maxItems, desiredItems);
    }, [containerWidth, currentBreakpoint, itemWidthValue, itemsDisplayed]);

    useScroll({
        container: scrollRef as RefObject<HTMLElement>,
        immediate: true,
        onChange: ({ value: { scrollX, scrollXProgress } }: ScrollChange) => {
            setScrollXProgress(scrollXProgress);
            setScrollX(scrollX);
        },
    });

    const canMoveBack = useMemo(() => {
        return (
            scrollX > Number(firstItemOffsetValue) + itemWidthValue / 10 &&
            scrollXProgress > 0
        );
    }, [firstItemOffsetValue, itemWidthValue, scrollX, scrollXProgress]);

    const canMoveForward = useMemo(
        () => scrollXProgress < 1,
        [scrollXProgress]
    );

    // Set by handleForward/handleBack, cleared by CarouselStatusAnnouncer once
    // it has announced the resulting position. See CarouselStatusAnnouncer.
    const pendingAnnouncementRef = useRef(false);

    const handleBack = useCallback(() => {
        pendingAnnouncementRef.current = true;
        scrollRef.current?.scrollTo({
            left:
                scrollRef.current?.scrollLeft -
                numItemsDisplayed * itemWidthValue,
            behavior: 'smooth',
        });
    }, [itemWidthValue, numItemsDisplayed]);

    const handleForward = useCallback(() => {
        pendingAnnouncementRef.current = true;
        scrollRef.current?.scrollTo({
            left:
                scrollRef.current?.scrollLeft +
                numItemsDisplayed * itemWidthValue,
            behavior: 'smooth',
        });
    }, [itemWidthValue, numItemsDisplayed]);

    const arrayChildren = Children.toArray(children);
    const [nonNullChildren, setNonNullChildren] = useState(
        new Set(arrayChildren.keys())
    );

    const handleWillBeNull = (index: number) => () => {
        setNonNullChildren((childrenSet) => {
            childrenSet.delete(index);
            return childrenSet;
        });
    };

    if (nonNullChildren.size === 0) {
        return <>{emptyState}</>;
    }

    return (
        <CarouselSpacingContainer
            itemSpacing={itemSpacingValue}
            willRemoveFirstAndLastVisibleItemSpacing={
                willRemoveFirstAndLastVisibleItemSpacing
            }
            {...props}
        >
            <DisplayedCarouselItemProvider
                displayedItems={arrayChildren}
                isFetched={true}
            >
                <Box
                    sx={sxAppend(styleContainer, sx)}
                    ref={containerRef}
                    onMouseEnter={handleHoverStart}
                    onMouseLeave={handleHoverEnd}
                    onFocus={handleHoverStart}
                    onBlur={handleHoverEnd}
                >
                    {showButtons && (
                        <Box
                            aria-hidden={!withAccessibleButtons}
                            sx={sxAppend(
                                styleButtonContainer,
                                toggleButtonsOnHover &&
                                    !isHovered &&
                                    styleButtonContainerInvisible,
                                buttonContainerSx
                            )}
                        >
                            <IconButton
                                trackButton={trackBackButton}
                                onClick={handleBack}
                                color="primary"
                                tabIndex={withAccessibleButtons ? 0 : -1}
                                aria-label={formatMessage(
                                    commonMessages['common.back']
                                )}
                                size={
                                    currentBreakpoint === 'xs' ||
                                    currentBreakpoint === 'sm'
                                        ? 'small'
                                        : 'legacyMedium'
                                }
                                disabled={!canMoveBack}
                                sx={sxAppend(
                                    styleButton,
                                    !canMoveBack &&
                                        !showButtonsWhenDisabled &&
                                        styleButtonInvisible
                                )}
                            >
                                <ArrowBackIos sx={styleArrowBackIosCorrector} />
                            </IconButton>
                            <IconButton
                                trackButton={trackForwardButton}
                                onClick={handleForward}
                                aria-label={formatMessage(
                                    commonMessages['common.forward']
                                )}
                                color="primary"
                                tabIndex={withAccessibleButtons ? 0 : -1}
                                disabled={!canMoveForward}
                                size={
                                    currentBreakpoint === 'xs' ||
                                    currentBreakpoint === 'sm'
                                        ? 'small'
                                        : 'legacyMedium'
                                }
                                sx={sxAppend(
                                    styleButton,
                                    !canMoveForward &&
                                        !showButtonsWhenDisabled &&
                                        styleButtonInvisible
                                )}
                            >
                                <ArrowForwardIos />
                            </IconButton>
                        </Box>
                    )}
                    <List
                        ref={scrollRef}
                        role={props.role || 'list'}
                        aria-roledescription={props['aria-roledescription']}
                        sx={sxAppend(
                            styleList,
                            styleNoScrollbar,
                            {
                                scrollPaddingLeft: `${firstItemOffsetValue}px`,
                                gridTemplateColumns: `repeat(${arrayChildren.length}, minmax(${itemWidthValue}px, 1fr))`,
                                gap: `${itemSpacingValue}px`,
                            },
                            showItemPreview && stylePreviewList,
                            !isSafariUser && styleScrollSnap,
                            listSx
                        )}
                        data-testid="carouselScrollContainer"
                    >
                        {arrayChildren.map((child, i) => {
                            return (
                                <WillBeNullProvider
                                    key={i}
                                    handleWillBeNull={handleWillBeNull(i)}
                                >
                                    <CarouselListItem
                                        position={i}
                                        itemWidth={itemWidth}
                                        scrollRef={scrollRef}
                                        itemWidthValue={itemWidthValue}
                                        trackItemScrollButton={
                                            trackItemScrollButton
                                        }
                                    >
                                        {child}
                                    </CarouselListItem>
                                </WillBeNullProvider>
                            );
                        })}
                    </List>
                    <CarouselStatusAnnouncer
                        total={arrayChildren.length}
                        pendingAnnouncementRef={pendingAnnouncementRef}
                    />
                </Box>
            </DisplayedCarouselItemProvider>
        </CarouselSpacingContainer>
    );
}
